<template>
  <div>
    <Navbar />

    <main class="auth-page">
      <div class="auth-bg">
        <div class="auth-bg-overlay"></div>
      </div>

      <div class="auth-container">
        <div class="auth-card">

          <!-- ── Success state ── -->
          <template v-if="done">
            <div class="success-state">
              <div class="success-icon-wrap">
                <font-awesome-icon icon="check-circle" class="success-icon" />
              </div>
              <h3 class="auth-title">Password changed!</h3>
              <p class="auth-sub">Your password has been updated. You can now sign in with your new credentials.</p>
              <router-link to="/auth/login" class="submit-btn" style="text-decoration:none; justify-content:center;">
                <font-awesome-icon icon="arrow-right" class="me-1" /> Go to Login
              </router-link>
            </div>
          </template>

          <!-- ── Form state ── -->
          <template v-else>
            <div class="auth-card-header">
              <div class="lock-icon-wrap">
                <font-awesome-icon icon="key" class="lock-icon" />
              </div>
              <h3 class="auth-title">Set a new password</h3>
              <p class="auth-sub">Choose a strong password for your account.</p>
            </div>

            <!-- Validation errors -->
            <div v-if="hasErrors" class="error-box">
              <div class="error-box-title">
                <font-awesome-icon icon="exclamation-circle" class="me-1" /> Please fix the following:
              </div>
              <ul class="error-list">
                <li v-for="(err, i) in flatErrors" :key="i">{{ err }}</li>
              </ul>
            </div>

            <form @submit.prevent="changePassword" novalidate>

              <!-- New password -->
              <div class="form-group-styled">
                <label class="form-label-styled">New Password <span class="req">*</span></label>
                <div class="pw-wrap">
                  <input
                    v-model="password"
                    :type="showPassword ? 'text' : 'password'"
                    class="form-input-styled"
                    :class="{ 'input--error': fieldError('password') }"
                    placeholder="Min. 8 characters"
                    autocomplete="new-password"
                    required
                  />
                  <button type="button" class="pw-toggle" @click="showPassword = !showPassword" tabindex="-1">
                    <font-awesome-icon :icon="showPassword ? 'eye-slash' : 'eye'" />
                  </button>
                </div>
                <p v-if="fieldError('password')" class="field-error">{{ fieldError('password') }}</p>

                <!-- Strength meter -->
                <div v-if="password" class="strength-wrap">
                  <div class="strength-bars">
                    <div class="strength-bar" :class="strengthClass(1)"></div>
                    <div class="strength-bar" :class="strengthClass(2)"></div>
                    <div class="strength-bar" :class="strengthClass(3)"></div>
                    <div class="strength-bar" :class="strengthClass(4)"></div>
                  </div>
                  <span class="strength-label" :class="'strength-label--' + strengthLevel.key">{{ strengthLevel.label }}</span>
                </div>
              </div>

              <!-- Confirm password -->
              <div class="form-group-styled">
                <label class="form-label-styled">Confirm New Password <span class="req">*</span></label>
                <div class="pw-wrap">
                  <input
                    v-model="password_confirmation"
                    :type="showConfirmPassword ? 'text' : 'password'"
                    class="form-input-styled"
                    :class="{ 'input--error': fieldError('password_confirmation'), 'input--warning': passwordMismatch }"
                    placeholder="Repeat new password"
                    autocomplete="new-password"
                    required
                  />
                  <button type="button" class="pw-toggle" @click="showConfirmPassword = !showConfirmPassword" tabindex="-1">
                    <font-awesome-icon :icon="showConfirmPassword ? 'eye-slash' : 'eye'" />
                  </button>
                </div>
                <p v-if="passwordMismatch" class="field-error">Passwords do not match.</p>
              </div>

              <button
                type="submit"
                class="submit-btn"
                :disabled="loading || !canSubmit"
              >
                <font-awesome-icon v-if="loading" icon="spinner" spin class="me-1" />
                {{ loading ? 'Updating password...' : 'Set New Password' }}
              </button>

            </form>

            <div class="auth-card-footer">
              <router-link to="/auth/login" class="back-link">
                <font-awesome-icon icon="arrow-left" class="me-1" /> Back to Login
              </router-link>
            </div>
          </template>

        </div>
      </div>
    </main>

    <Footer />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import axios from 'axios';
import { useRoute } from 'vue-router';
import { useToast } from 'vue-toastification';
import Navbar from '../components/Navbar';
import Footer from '../components/footer';

export default {
  name: 'ResetPassword',

  components: { Navbar, Footer },

  setup() {
    // const router = useRouter();
    const route  = useRoute();
    const toast  = useToast();

    const password              = ref('');
    const password_confirmation = ref('');
    const loading               = ref(false);
    const done                  = ref(false);
    const showPassword          = ref(false);
    const showConfirmPassword   = ref(false);
    const validationErrors      = ref({});

    // ── Computed ─────────────────────────────────────────────────────
    const passwordMismatch = computed(() =>
      password_confirmation.value.length > 0 &&
      password.value !== password_confirmation.value
    );

    const passwordStrength = computed(() => {
      const p = password.value;
      if (!p) return 0;
      let score = 0;
      if (p.length >= 8)                    score++;
      if (/[A-Z]/.test(p))                  score++;
      if (/[0-9]/.test(p))                  score++;
      if (/[^A-Za-z0-9]/.test(p))           score++;
      return score;
    });

    const strengthLevel = computed(() => {
      const levels = [
        { key: 'weak',      label: 'Weak'      },
        { key: 'fair',      label: 'Fair'      },
        { key: 'good',      label: 'Good'      },
        { key: 'strong',    label: 'Strong'    },
        { key: 'strong',    label: 'Strong'    },
      ];
      return levels[passwordStrength.value] ?? levels[0];
    });

    const strengthClass = (bar) => {
      const score = passwordStrength.value;
      if (score === 0) return '';
      if (score === 1) return bar <= 1 ? 'bar--weak'   : '';
      if (score === 2) return bar <= 2 ? 'bar--fair'   : '';
      if (score === 3) return bar <= 3 ? 'bar--good'   : '';
      return bar <= 4 ? 'bar--strong' : '';
    };

    const canSubmit = computed(() =>
      password.value.length >= 8 &&
      password_confirmation.value &&
      !passwordMismatch.value
    );

    const hasErrors  = computed(() => Object.keys(validationErrors.value).length > 0);
    const flatErrors = computed(() => Object.values(validationErrors.value).flat());
    const fieldError = (field) => validationErrors.value[field]?.[0] ?? null;

    const normaliseErrors = (msg) => {
      if (!msg) return {};
      if (typeof msg === 'string')  return { general: [msg] };
      if (Array.isArray(msg))       return { general: msg.flat() };
      return msg;
    };

    // ── Submit ────────────────────────────────────────────────────────
    const changePassword = async () => {
      if (!canSubmit.value) return;
      loading.value = true;
      validationErrors.value = {};

      try {
        await axios.post('/reset-password', {
          password:              password.value,
          password_confirmation: password_confirmation.value,
          resetToken:            route.params.token,
        });
        done.value = true;
        toast.success('Password changed successfully.');
      } catch (err) {
        const status  = err.response?.status;
        const message = err.response?.data?.message;
        if (status === 422) {
          validationErrors.value = normaliseErrors(message);
          toast.error('Please fix the errors below.');
        } else if (status === 400) {
          validationErrors.value = normaliseErrors(message);
          toast.error(message ?? 'Invalid or expired reset link.');
        } else {
          validationErrors.value = normaliseErrors(message);
          toast.error(message ?? 'An error occurred. Please try again.');
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      password, password_confirmation,
      loading, done,
      showPassword, showConfirmPassword,
      validationErrors, hasErrors, flatErrors, fieldError,
      passwordMismatch, canSubmit,
      passwordStrength, strengthLevel, strengthClass,
      changePassword,
    };
  },
};
</script>

<style scoped>
/* ── Page shell ──────────────────────────────────────────────────────── */
.auth-page {
  min-height: 100vh;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: url('/static/img/banner-bg-5.jpg') center/cover no-repeat;
}
.auth-bg-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(5,27,53,.96) 0%, rgba(0,74,173,.82) 100%);
}

.auth-container {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 40px 24px;
  min-height: 100vh;
}

/* ── Card ────────────────────────────────────────────────────────────── */
.auth-card {
  background: #fff;
  border-radius: 16px;
  padding: 44px 40px;
  width: 100%;
  max-width: 460px;
  box-shadow: 0 24px 60px rgba(0,0,0,.22);
}

/* ── Header ──────────────────────────────────────────────────────────── */
.auth-card-header { margin-bottom:26px; text-align:center; }
.lock-icon-wrap   { width:60px; height:60px; border-radius:50%; background:rgba(0,74,173,.1); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:22px; margin:0 auto 18px; }
.lock-icon        { font-size:22px; }
.auth-title       { font-size:22px; font-weight:800; color:#1a2b45; margin:0 0 8px; }
.auth-sub         { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.5; }
.req              { color:#e03535; }
.me-1             { margin-right:4px; }

/* ── Error box ───────────────────────────────────────────────────────── */
.error-box       { background:#fff5f5; border-left:3px solid #e03535; border-radius:0 8px 8px 0; padding:12px 14px; margin-bottom:20px; }
.error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:6px; display:flex; align-items:center; }
.error-list      { margin:0; padding-left:16px; }
.error-list li   { font-size:12.5px; color:#c02828; margin-bottom:3px; }

/* ── Form ────────────────────────────────────────────────────────────── */
.form-group-styled { display:flex; flex-direction:column; gap:6px; margin-bottom:16px; }
.form-label-styled { font-size:13px; font-weight:600; color:#1a2b45; }

.form-input-styled {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #dde5f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1a2b45;
  background: #fff;
  outline: none;
  transition: border-color .15s, box-shadow .15s;
  font-family: inherit;
}
.form-input-styled:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.09); }
.input--error   { border-color:#e03535 !important; }
.input--warning { border-color:#e08c00 !important; }
.field-error    { font-size:12px; color:#e03535; margin:2px 0 0; }

/* ── Password ────────────────────────────────────────────────────────── */
.pw-wrap { position:relative; display:flex; align-items:center; }
.pw-wrap .form-input-styled { padding-right:42px; }
.pw-toggle { position:absolute; right:12px; border:none; background:transparent; color:#7a90b0; cursor:pointer; font-size:14px; padding:0; transition:color .15s; }
.pw-toggle:hover { color:#1a2b45; }

/* ── Strength meter ──────────────────────────────────────────────────── */
.strength-wrap  { display:flex; align-items:center; gap:10px; margin-top:6px; }
.strength-bars  { display:flex; gap:4px; flex:1; }
.strength-bar   { height:4px; flex:1; border-radius:2px; background:#e4eaf2; transition:background .2s; }
.bar--weak      { background:#e03535; }
.bar--fair      { background:#e08c00; }
.bar--good      { background:#3b82f6; }
.bar--strong    { background:#16a34a; }
.strength-label { font-size:11.5px; font-weight:700; white-space:nowrap; }
.strength-label--weak   { color:#e03535; }
.strength-label--fair   { color:#e08c00; }
.strength-label--good   { color:#3b82f6; }
.strength-label--strong { color:#16a34a; }

/* ── Submit ──────────────────────────────────────────────────────────── */
.submit-btn {
  width: 100%;
  padding: 13px;
  background: #004AAD;
  color: #fff;
  border: none;
  border-radius: 9px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: background .15s, transform .1s;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}
.submit-btn:hover:not(:disabled)  { background:#003b8e; }
.submit-btn:active:not(:disabled) { transform:scale(.98); }
.submit-btn:disabled { opacity:.5; cursor:not-allowed; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.auth-card-footer { margin-top:20px; padding-top:16px; border-top:1px solid #f0f4f8; display:flex; justify-content:center; }
.back-link { display:inline-flex; align-items:center; gap:5px; color:#7a90b0; font-weight:600; text-decoration:none; font-size:13px; transition:color .15s; }
.back-link:hover { color:#004AAD; }

/* ── Success state ───────────────────────────────────────────────────── */
.success-state     { display:flex; flex-direction:column; align-items:center; text-align:center; gap:18px; }
.success-icon-wrap { width:72px; height:72px; border-radius:50%; background:rgba(22,163,74,.1); color:#16a34a; display:flex; align-items:center; justify-content:center; font-size:28px; }
.success-icon      { font-size:28px; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .auth-card      { padding:32px 20px; }
  .auth-container { padding:24px 16px; }
}
</style>